import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Overlay from "../../../Overlay";
import { Form, Formik } from "formik";
import ComboBox from "../../../common/ComboBox";
import TextBox from "../../../common/TextBox";
import Button from "../../../common/Button";
import useLandingPage from "../../../../hooks/useLandingPage";
import useWorks from "../../../../hooks/useWorks";
import { startOfDay } from "date-fns";
import Priorities from "./Priorities";
import DatePicker from "../../../common/DatePicker";
import { PAGE_STATE } from "../../../../constants";
import { toast } from "react-toastify";

const ManagePriorities = ({ section, onClose }) => {
  const {
    queryLandingPageConfigs,
    queryPriorities,
    createPriority,
    patchPriority,
  } = useLandingPage();
  const { queryWorks } = useWorks();

  const [configs, setConfigs] = useState(undefined);
  const [newConfigs, setNewConfigs] = useState(undefined);
  const [loadingState, setLoadingState] = useState(PAGE_STATE.IDLE);

  useEffect(() => {
    fetchLandingPageConfigs(section, startOfDay(new Date()));
  }, []);

  const fetchLandingPageConfigs = async (section, date) => {
    setLoadingState(PAGE_STATE.LOADING);

    const { data: landingPageConfigs } = await queryLandingPageConfigs(
      {
        effectiveDate: {
          $lte: startOfDay(date),
        },
        dueDate: {
          $gte: startOfDay(date),
        },
        section,
      },
      null,
      -1,
      {
        price: -1,
      }
    );

    const { data: works } = await queryWorks({
      _id: { $in: landingPageConfigs.map((lpc) => lpc.work) },
    });

    const { data: priorities } = await queryPriorities(
      {
        landingPageConfigId: { $in: landingPageConfigs.map((lpc) => lpc._id) },
        date: {
          $gte: startOfDay(new Date()),
          $lte: startOfDay(new Date()),
        },
      },
      null,
      -1
    );

    setConfigs(
      landingPageConfigs.map((c, i) => {
        // * Map work
        const _work = works.find((w) => w._id === c.work);
        c.work = _work;

        // * Map priority
        const _priority = priorities.find(
          (p) => p.landingPageConfigId === c._id
        );

        if (_priority) {
          c.order = _priority.value;
          c.oldOrder = _priority.value;
          c.naturalOrder = false;
          c.priorityId = _priority._id;
        }

        if (!_priority) {
          c.order = i + 1;
          c.oldOrder = i + 1;
          c.naturalOrder = true;
        }

        return c;
      })
    );
    setLoadingState(PAGE_STATE.IDLE);
  };

  const handleValidate = ({ date }) => {
    let errors = {};
    if (!date) errors.date = "Select date";

    // * Fetch landing page configs and their priorities
    if (date) fetchLandingPageConfigs(section, date);

    return errors;
  };

  const handleSubmit = ({ date }, options) => {
    if (!newConfigs) {
      toast.success(`Success, no configs were changed`);
      return;
    }

    let requests = [];

    for (const {
      _id,
      order,
      oldOrder,
      naturalOrder,
      priorityId,
    } of newConfigs) {
      // * Skip if order didn't change
      if (order === oldOrder) continue;

      // * Natural order; create new priority
      if (naturalOrder) {
        requests.push(createPriority(_id, startOfDay(new Date()), order));
      }

      if (!naturalOrder) {
        requests.push(patchPriority(_id, priorityId, order));
      }
    }

    if (requests.length === 0) {
      return;
    }

    Promise.allSettled(requests).then((results) => {
      const failedRequests = results.filter((r) => r.status !== "fulfilled");

      if (failedRequests.length > 0) {
        toast.warn(`Success but have ${failedRequests.length} fail.`);
        return;
      }
      toast.success(`Success!`);

      onClose();
    });
  };

  const handleOrderChange = (configs) => {
    setNewConfigs(configs);
  };

  const handleClose = () => {
    onClose();
  };

  const orderOptions = !configs
    ? []
    : configs.map((_, i) => ({
        name: (i + 1).toString(),
        value: i + 1,
      }));

  return (
    <Overlay
      style={{
        alignItems: "flex-start",
      }}
      content={
        <div className="admin-lp-hb-manage-priorities">
          <h1 className="admin-lp-hb-manage-priorities__title">
            Manage Priorities
          </h1>
          <Formik
            initialValues={{
              date: startOfDay(new Date()),
            }}
            validate={handleValidate}
            onSubmit={handleSubmit}
          >
            {(props) => {
              return (
                <Form className="admin-lp-hb-manage-priorities-form">
                  <DatePicker
                    name="date"
                    label="Date"
                    dateFormat="dd/MM/yyyy"
                  />
                  {loadingState === PAGE_STATE.LOADING ? (
                    <>Loading</>
                  ) : (
                    configs && (
                      <div className="admin-lp-hb-manage-priorities-form-manage">
                        <div className="admin-lp-hb-manage-priorities-form-manage__header">
                          Orders
                        </div>
                        <Priorities
                          defaultConfigs={configs}
                          orderOptions={orderOptions}
                          onChange={handleOrderChange}
                        />
                      </div>
                    )
                  )}
                  <div className="admin-lp-hb-manage-priorities-form-footer">
                    <Button
                      label={"Cancel"}
                      variant={"secondary"}
                      onClick={handleClose}
                    />
                    <div style={{ flexGrow: 1 }}></div>
                    <Button label={"Save"} type="submit" />
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      }
      onClose={onClose}
    />
  );
};

ManagePriorities.propTypes = {
  onClose: PropTypes.func,
};

ManagePriorities.defaultProps = {
  onClose: () => {},
};

export default ManagePriorities;
